/**
 * LionRapid Builder - Enhanced with explicit namespace preloading
 */

import { EventEmitter } from './base/event-emitter.js';
import type { LionRapidCoreConfig } from './config/lionrapid.config.js';
import type { NetworkRepositoryConfig } from './config/repository.config.js';
import { HandlerChain } from './handlers/handler-chain.js';
import { AsyncHandler, NetworkHandler, SyncHandler } from './handlers/index.js';
import type {
  IEmbeddedPlugin,
  IPlugin,
} from './interfaces/plugin.interface.js';
import {
  IAsyncRepository,
  ISyncRepository,
} from './interfaces/repository.interface.js';
import { LionRapidCore } from './lionRapid.js';
import {
  TranslationLockedError,
  TranslationNotFoundError,
} from './errors/translation-errors.js';
import {
  StaticRepository,
  type StaticBundle,
} from './repositories/static-repository.js';
import { MissingTranslationQueue } from './sync/missing-translation-queue.js';
import { logger } from './utils/logger.js';

/**
 * True when a preload failure deserves a loud console diagnostic: network or
 * server trouble the integrator should hear about. 404 (empty project) and
 * 423 (content still being prepared) are routine states, not failures.
 * The internal logger is compiled out of production bundles, so genuine
 * failures must reach console.warn explicitly.
 */
export function isLoudPreloadFailure(error: unknown): boolean {
  return !(
    error instanceof TranslationNotFoundError ||
    error instanceof TranslationLockedError
  );
}

export class LionRapidBuilder {
  private config: LionRapidCoreConfig;
  private plugins: IPlugin[] = [];
  private syncStorageRepos: ISyncRepository[] = [];
  private asyncStorageRepos: IAsyncRepository[] = [];
  private networkRepos: IAsyncRepository[] = [];
  private syncQueue: MissingTranslationQueue | null = null;

  constructor(config: LionRapidCoreConfig) {
    this.config = {
      defaultLocale: config.defaultLocale || 'en',
      namespace: config.namespace || 'app',
    };
  }

  static init(config: LionRapidCoreConfig = {}): LionRapidBuilder {
    return new LionRapidBuilder(config);
  }

  private useSyncStorage(memory: ISyncRepository): LionRapidBuilder {
    this.syncStorageRepos.push(memory);
    return this;
  }

  private useAsyncStorage(storage: IAsyncRepository): LionRapidBuilder {
    this.asyncStorageRepos.push(storage);
    return this;
  }

  private useNetwork(network: IAsyncRepository): LionRapidBuilder {
    this.networkRepos.push(network);
    return this;
  }

  use(
    instance: IPlugin | IEmbeddedPlugin | ISyncRepository | IAsyncRepository
  ): LionRapidBuilder {
    switch (instance.type) {
      case 'plugin':
        this.plugins.push(instance);
        break;
      case 'memory':
        this.useSyncStorage(instance);
        break;
      case 'storage':
        this.useAsyncStorage(instance);
        break;
      case 'network':
        this.useNetwork(instance);
        break;
    }
    return this;
  }

  /**
   * Offline resilience: seed a translation bundle (or the CLI's snapshot files)
   * as a read-only sync layer that resolves synchronously even with the server
   * down and nothing cached. Sits after the live memory layer (priority 0) at
   * `priority` (default 0.5), so fresh network data always shadows it.
   */
  useStaticBundle(bundle: StaticBundle, priority = 0.5): LionRapidBuilder {
    return this.use(new StaticRepository(bundle, { priority }));
  }

  async build(): Promise<LionRapidCore> {
    const eventEmitter = new EventEmitter();
    const buildBus = eventEmitter.builder();

    logger.info('Builder started');
    logger.debug('Builder config', this.config);
    buildBus.buildStart(this.config.defaultLocale!, this.config.namespace!);

    const handlerChain = new HandlerChain(eventEmitter);
    // Always add memory cache handler first
    for (const syncRepo of this.syncStorageRepos) {
      if (!syncRepo.getConfig().enabled) continue;
      const syncHandler = new SyncHandler(eventEmitter, syncRepo);
      buildBus.use('sync');
      handlerChain.addSyncHandler(syncHandler);
    }
    // Always add storage cache handler second
    for (const asyncRepo of this.asyncStorageRepos) {
      if (!asyncRepo.getConfig().enabled) continue;
      const asyncHandler = new AsyncHandler(eventEmitter, asyncRepo);
      buildBus.use('async');
      handlerChain.addAsyncHandler(asyncHandler);
    }

    // Always add network cache handler last
    let networkFirst: NetworkHandler | null = null;
    for (const networkRepo of this.networkRepos) {
      if (
        !networkRepo.getConfig().enabled ||
        !networkRepo.getConfig().options?.baseUrl
      )
        continue;
      const networkHandler = new NetworkHandler(eventEmitter, networkRepo);
      if (!networkFirst) {
        networkFirst = networkHandler;
      }
      buildBus.use('network');
      handlerChain.addAsyncHandler(networkHandler);

      // ✅ Initialize missing translation sync queue if enabled
      const config = networkRepo.getConfig() as NetworkRepositoryConfig;
      if (config.options?.syncMissing?.enabled) {
        this.syncQueue = new MissingTranslationQueue(networkRepo, {
          enabled: config.options.syncMissing.enabled,
          debounceMs: config.options.syncMissing.debounceMs || 500,
          batchSize: config.options.syncMissing.batchSize || 50,
        }, eventEmitter);

        // Subscribe to translation:not-found events (404 only)
        // Note: translation:locked (423) is NOT synced since translation exists but is not ready
        eventEmitter.on('translation:not-found', envelope => {
          if (this.syncQueue) {
            this.syncQueue.add({
              key: envelope.payload.key,
              fallbackValue: envelope.payload.fallbackValue,
              styles: envelope.payload.styles,
              locale: envelope.payload.locale,
              namespace: envelope.payload.namespace,
            });
          }
        });

        logger.info('Missing translation sync enabled');
        logger.debug('Sync queue config', config.options.syncMissing);
      }
    }

    logger.debug('Handler chain constructed');

    // Create facade
    const facade = new LionRapidCore(
      eventEmitter,
      handlerChain,
      this.config.defaultLocale,
      this.config.namespace
    );

    // Initialize plugins and add their handlers
    for (const plugin of this.plugins) {
      buildBus.use('plugin');
      await plugin.initialize({ core: facade, bus: eventEmitter });
      facade.registerPlugin(plugin);
      logger.info(`Plugin initialized: ${plugin.name}`);
      logger.debug(`Plugin version: ${plugin.version}`, plugin.capabilities);
    }

    const chainInfo = handlerChain.getChainInfo();

    // ✅ Explicit namespace preloading
    if (networkFirst) {
      await this.executePreloading(networkFirst, handlerChain);
    }
    logger.info('Builder completed');
    buildBus.buildDone(
      chainInfo.handlers.map(r => r.name),
      this.plugins.map(p => p.name)
    );

    return facade;
  }

  // ✅ CLEAN: Preloading logic
  private async executePreloading(
    networkHandler: NetworkHandler,
    handlerChain: HandlerChain
  ): Promise<void> {
    const eagerPreloads = networkHandler
      .getPreload()
      .filter(config => config.eager !== false);

    if (!eagerPreloads || eagerPreloads.length === 0) {
      return;
    }

    logger.info(`Preloading ${eagerPreloads.length} namespaces`);
    logger.debug('Preload targets', {
      namespaces: eagerPreloads.map(c => `${c.locale}:${c.namespace}`),
    });

    let warnedLoudly = false;
    const warnLoudlyOnce = (namespaceKey: string, error: unknown) => {
      if (!isLoudPreloadFailure(error) || warnedLoudly) return;
      warnedLoudly = true;
      // eslint-disable-next-line no-console
      // Name the HOST and the way to change it. A default that points at an
      // unreachable host is precisely when "the server is reachable" is not
      // enough for the reader to act on — they need to know which server was
      // tried and which attribute overrides it.
      const baseUrl = networkHandler.getBaseUrl() || '(no base URL configured)';
      console.warn(
        `[LionRapid] Could not load translations for ${namespaceKey} from ` +
          `${baseUrl} — the page stays untranslated until that host is ` +
          'reachable. If it is wrong, set data-base-url on the snippet tag ' +
          '(or `baseUrl` in the network repository options).',
        error
      );
    };
    const promises = eagerPreloads.map(async preloadConfig => {
      const namespaceKey = `${preloadConfig.locale}:${preloadConfig.namespace}`;
      try {
        const result = await networkHandler.loadNamespace(
          preloadConfig.locale,
          preloadConfig.namespace
        );

        if (result && result.data && result.status === 200) {
          const context = {
            locale: preloadConfig.locale,
            namespace: preloadConfig.namespace,
          };

          await handlerChain.saveNamespaceToHandlers(result.data, context);
          logger.debug(`Preloaded: ${namespaceKey}`);
          return;
        }

        // loadNamespace never throws — failures come back as a fallback
        // result with the error attached (status 0 = network/transport).
        if (result?.error) {
          warnLoudlyOnce(namespaceKey, result.error);
          logger.warn(`Preload failed: ${namespaceKey}`, result.error);
        }
      } catch (error) {
        warnLoudlyOnce(namespaceKey, error);
        logger.warn(`Preload failed: ${namespaceKey}`, error);
      }
    });

    await Promise.allSettled(promises);
    logger.info('Preloading completed');
  }
}
